import useTranslator from "@pma/hooks/use-translator";
import { DictType } from "@pma/types";
import { useCallback, useEffect, useRef, useState } from "react";
import { Marker, MarkerDragEvent, Popup } from "react-map-gl";
import { Separator } from "@components/common/separator";
import { Button } from "@components/common/button";
import { useNavigate, useSearchParams } from "react-router-dom";
import { cn } from "@pma/utils/helpers";
import { ScrollArea } from "@components/common/scroll-area";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@components/common/accordion";

interface Props {
  task: DictType;
}
const TaskMarker = ({ task }: Props) => {
  const [marker, setMarker] = useState<DictType>({
    longitude: task?._gis?.data?.longitude,
    latitude: task?._gis?.data?.latitude,
  });
  const [showTaskMeta, setShowTaskMeta] = useState(false);
  const [_, logEvents] = useState<DictType>({});

  const { translate } = useTranslator();
  const navigate = useNavigate();
  const markerRef = useRef<any>(null);

  const [searchParam] = useSearchParams();

  const paramLng = searchParam.get("lng");
  const paramLat = searchParam.get("lat");

  const gisData = task?._gis?.data;

  const focusMarker =
    String(gisData?.latitude) === paramLat &&
    String(gisData?.longitude) === paramLng;

  const onMarkerDragStart = useCallback((event: MarkerDragEvent) => {
    logEvents((_events) => ({ ..._events, onDragStart: event.lngLat }));
  }, []);

  const onMarkerDrag = useCallback((event: MarkerDragEvent) => {
    logEvents((_events) => ({ ..._events, onDrag: event.lngLat }));

    setMarker({
      longitude: event.lngLat.lng,
      latitude: event.lngLat.lat,
    });
  }, []);

  const onMarkerDragEnd = useCallback((event: MarkerDragEvent) => {
    setMarker({
      longitude: event.lngLat.lng,
      latitude: event.lngLat.lat,
    });
    logEvents((_events) => ({ ..._events, onDragEnd: event.lngLat }));
  }, []);
  const handleFocus = () => {
    if (markerRef.current) {
      const markerElement = markerRef.current.querySelector(`#task-${task.id}`);

      setShowTaskMeta(true);
      if (markerElement) {
        // Scroll to the parent element containing the nearest id

        markerElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  };
  useEffect(() => {
    if (focusMarker) {
      handleFocus();
    }
  }, [focusMarker]);

  return (
    <Marker
      latitude={marker?.latitude}
      longitude={marker?.longitude}
      onClick={(e) => {
        e.originalEvent.stopPropagation();

        setShowTaskMeta((prev) => !prev);
      }}
      // draggable
      style={{ cursor: "pointer" }}
      onDragStart={onMarkerDragStart}
      onDrag={onMarkerDrag}
      onDragEnd={onMarkerDragEnd}
    >
      {showTaskMeta && (
        <Popup
          latitude={marker?.latitude}
          longitude={marker?.longitude}
          onClose={() => setShowTaskMeta(false)}
          closeOnMove
          focusAfterOpen
        >
          <div className="w-[300px]">
            <div className="flex flex-wrap items-center justify-between">
              <h4 className="font-semibold text-lg">{task?.title}</h4>

              <Button
                variant={"link"}
                size={"sm"}
                onClick={() => navigate(`?task=${task?.id}`)}
              >
                {translate("Go to task")}
              </Button>
            </div>
            <Separator />

            <Accordion
              defaultValue="properties"
              type="single"
              collapsible
              className="w-full"
            >
              {gisData?.details?.layer && (
                <AccordionItem value="layers">
                  <AccordionTrigger>{translate("Layer")}</AccordionTrigger>
                  <AccordionContent>
                    {Object.keys(gisData?.details?.layer)?.map(
                      (attr, index) => (
                        <div
                          key={index}
                          className="flex w-full justify-between"
                        >
                          <div className="capitalize font-medium border flex-1 p-1">
                            {attr
                              .toLocaleLowerCase()
                              .replaceAll("_", " ")
                              .replaceAll("-", " ")}
                          </div>

                          <div className="text-left border flex-1 p-1">
                            {typeof gisData?.details?.layer[`${attr}`] ==
                            "object"
                              ? "---"
                              : gisData?.details?.layer[`${attr}`]}
                          </div>
                        </div>
                      ),
                    )}
                  </AccordionContent>
                </AccordionItem>
              )}
              {gisData?.details?.geometry && (
                <AccordionItem value="geometry">
                  <AccordionTrigger>{translate("Geometry")}</AccordionTrigger>
                  <AccordionContent>
                    <div className="flex w-full justify-between">
                      <div className="capitalize font-medium border flex-1 p-1">
                        {translate("Longitude")}
                      </div>

                      <div className="text-left border flex-1 p-1">
                        {gisData?.longitude}
                      </div>
                    </div>
                    <div className="flex w-full justify-between">
                      <div className="capitalize font-medium border flex-1 p-1">
                        {translate("Latitude")}
                      </div>

                      <div className="text-left border flex-1 p-1">
                        {gisData?.latitude}
                      </div>
                    </div>
                    {Object.keys(gisData?.details?.geometry)?.map(
                      (attr, index) => (
                        <div
                          key={index}
                          className="flex w-full justify-between"
                        >
                          <div className="capitalize font-medium border flex-1 p-1">
                            {attr
                              .toLocaleLowerCase()
                              .replaceAll("_", " ")
                              .replaceAll("-", " ")}
                          </div>

                          <div className="text-left border flex-1 p-1">
                            {typeof gisData?.details?.geometry[`${attr}`] ==
                            "object"
                              ? "---"
                              : gisData?.details?.geometry[`${attr}`]}
                          </div>
                        </div>
                      ),
                    )}
                  </AccordionContent>
                </AccordionItem>
              )}
              <AccordionItem value="properties">
                <AccordionTrigger>{translate("Properties")}</AccordionTrigger>
                <AccordionContent>
                  <ScrollArea className="flex flex-col h-[400px] pb-6">
                    {gisData?.details?.properties ? (
                      <>
                        {Object.keys(gisData?.details?.properties)?.map(
                          (attr, index) => (
                            <div
                              key={index}
                              className="flex w-full justify-between"
                            >
                              <div className="capitalize font-medium border flex-1 p-1">
                                {attr
                                  .toLocaleLowerCase()
                                  .replaceAll("_", " ")
                                  .replaceAll("-", " ")}
                              </div>

                              <div className="text-left border flex-1 p-1">
                                {typeof gisData?.details?.properties[
                                  `${attr}`
                                ] == "object"
                                  ? "---"
                                  : gisData?.details?.properties[`${attr}`]}
                              </div>
                            </div>
                          ),
                        )}
                      </>
                    ) : (
                      <>
                        {/* This is only there to account for previously created records 15-01-2025 */}
                        {gisData?.details &&
                          Object.keys(gisData?.details)?.map((attr, index) => (
                            <div
                              key={index}
                              className="flex w-full justify-between"
                            >
                              <div className="capitalize font-medium border flex-1 p-1">
                                {attr
                                  .toLocaleLowerCase()
                                  .replaceAll("_", " ")
                                  .replaceAll("-", " ")}
                              </div>

                              <div className="text-left border flex-1 p-1">
                                {typeof gisData?.details[`${attr}`] == "object"
                                  ? "---"
                                  : gisData?.details[`${attr}`]}
                              </div>
                            </div>
                          ))}
                      </>
                    )}
                  </ScrollArea>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </Popup>
      )}
      <img
        onClick={() => setShowTaskMeta(true)}
        className={cn("h-16 w-16")}
        src={
          focusMarker
            ? `${process.env.PUBLIC_URL}/marker-1.png`
            : `${process.env.PUBLIC_URL}/marker.png`
        }
      />
    </Marker>
  );
};

export default TaskMarker;
